import React, {useState} from "react"
import {useLazyQuery, useMutation} from "@apollo/client"
import subtitleSearch from "graphql/Media/Subtitle/query/subtitleSearch"
import subtitleImportOpenSubtitles from "graphql/Media/Subtitle/mutation/subtitleImportOpenSubtitles"

import "./style.scss"

type SubtitleManagerProps = {
  mediaId: string,
  subtitles: any[],
  onImported: () => Promise<any>,
  hideHeading?: boolean,
}

const matchLabel = (matchType: string) => ({
  HASH: "Fichier exact",
  TMDB: "Correspondance TMDB",
  METADATA: "Titre et épisode",
}[matchType] || matchType)

export const SubtitleManager = ({mediaId, subtitles = [], onImported, hideHeading = false}: SubtitleManagerProps) => {
  const [language, setLanguage] = useState("fr")
  const [forcedOnly, setForcedOnly] = useState(false)
  const [importingFileId, setImportingFileId] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const [search, {data, loading, error, called}] = useLazyQuery(subtitleSearch, {
    fetchPolicy: "no-cache",
  })
  const [importSubtitle] = useMutation(subtitleImportOpenSubtitles)

  const runSearch = () => search({variables: {mediaId, language, forcedOnly}})
  const results = data?.results || []

  const install = async (result: any) => {
    setImportingFileId(result.fileId)
    setImportError(null)
    try {
      await importSubtitle({
        variables: {
          mediaId,
          fileId: result.fileId,
          language: result.language || language,
        },
      })
      await onImported()
    } catch (importFailure) {
      setImportError(importFailure instanceof Error ? importFailure.message : String(importFailure))
    } finally {
      setImportingFileId(null)
    }
  }

  return <section className="subtitle-manager">
    {!hideHeading && <div className="subtitle-manager-heading">
      <div>
        <span className="bo-eyebrow">OpenSubtitles</span>
        <h2>Sous-titres</h2>
        <p>Rechercher une piste correspondant à ce fichier ou à ses métadonnées.</p>
      </div>
      <span className="fas fa-closed-captioning"></span>
    </div>}

    {subtitles.length > 0 && <div className="subtitle-installed-list">
      {subtitles.map(subtitle => <div className="subtitle-installed" key={subtitle.id}>
        <span className="fas fa-check"></span>
        <strong>{subtitle.language?.toUpperCase() || "Langue inconnue"}</strong>
        <small>{subtitle.offset ? `Décalage ${subtitle.offset} ms` : "Installé"}</small>
      </div>)}
    </div>}

    <div className="subtitle-search-controls">
      <label>
        Langue
        <select className="form-control" value={language} onChange={event => setLanguage(event.target.value)}>
          <option value="fr">Français</option>
          <option value="en">Anglais</option>
          <option value="es">Espagnol</option>
          <option value="de">Allemand</option>
          <option value="it">Italien</option>
          <option value="pt-pt">Portugais</option>
        </select>
      </label>
      <label className="subtitle-forced-control">
        <input type="checkbox" checked={forcedOnly} onChange={event => setForcedOnly(event.target.checked)} />
        Dialogues étrangers uniquement
      </label>
      <button type="button" className="subtitle-search-button" disabled={loading} onClick={runSearch}>
        {loading ? "Recherche…" : "Rechercher"}
      </button>
    </div>

    {error && <div className="subtitle-message subtitle-message-error">{error.message}</div>}
    {importError && <div className="subtitle-message subtitle-message-error">{importError}</div>}
    {called && !loading && !error && results.length === 0 && <div className="subtitle-message">Aucun sous-titre correspondant trouvé.</div>}

    {results.length > 0 && <div className="subtitle-result-list">
      {results.map((result: any) => <article className="subtitle-result" key={result.fileId}>
        <div className="subtitle-result-main">
          <div className="subtitle-result-title">
            <strong>{result.release || result.fileName || result.featureTitle || `Sous-titre ${result.fileId}`}</strong>
            <span>{result.language?.toUpperCase()}</span>
          </div>
          <small>{[result.featureTitle, result.year, result.fileName].filter(Boolean).join(" · ")}</small>
          <div className="subtitle-result-tags">
            <span className={`subtitle-match subtitle-match-${result.matchType?.toLowerCase()}`}>{matchLabel(result.matchType)}</span>
            {result.forced && <span>Forcé</span>}
            {result.hearingImpaired && <span>Malentendants</span>}
            {result.trusted && <span>Source fiable</span>}
            {(result.aiTranslated || result.machineTranslated) && <span>Traduit automatiquement</span>}
            <span>{result.downloadCount} téléchargements</span>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-light"
          disabled={importingFileId !== null}
          onClick={() => install(result)}
        >
          {importingFileId === result.fileId ? "Installation…" : "Installer"}
        </button>
      </article>)}
    </div>}
  </section>
}
